import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Box,
  Card,
  Dialog,
  Button,
  IconButton,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { useChangeUserPasswordSystemMutation } from "../../../Api/attoDeskApi";
import CloseIcon from "@mui/icons-material/Close";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { IUserAdmin } from "../../../Api/Interface/api.interface";
import useIsMobile from "../../../Core/hook/useIsMobile";

interface FormValues {
  userId: string;
  new_password: string;
  confirmPassword: string;
}


const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobileRes = useIsMobile();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [newCompany, { isLoading }] = useChangeUserPasswordSystemMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordCf, setShowPasswordCf] = useState(false);

  const formik = useFormik<FormValues>({
    initialValues: {
      userId: "",
      new_password: "",
      confirmPassword: "",
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: any = {};
      if (values.new_password !== values.confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = {
          userId: values.userId,
          new_password: values.new_password,
          new_password_confirmation: values.confirmPassword,
        };

        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          resetForm();
          localStorage.setItem("token", response.token);
          window.location.reload();

        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleClickCfPassword = () => {
    setShowPasswordCf((prev) => !prev);
  };

  const formValid = useMemo(() => {
    return (
      formik.values.new_password !== "" &&
      formik.values.new_password !== undefined &&
      formik.values.confirmPassword !== "" &&
      formik.values.confirmPassword !== undefined &&
      formik.values.new_password === formik.values.confirmPassword &&
      !formik.errors.confirmPassword
    );
  }, [
    formik.values.confirmPassword,
    formik.values.new_password,
    formik.errors,
  ]);

  const cancelFunction = () => {
    formik.resetForm();
  };

  return (
    <Grid container spacing={3}>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Reset Password
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Password{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Enter Password"
                            size="small"
                            type={showPassword ? "text" : "password"}
                            {...formik.getFieldProps("new_password")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={handleClickShowPassword}
                                    edge="end"
                                  >
                                    {showPassword ? (
                                      <Visibility />
                                    ) : (
                                      <VisibilityOff />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            error={
                              formik.touched.new_password &&
                              Boolean(formik.errors.new_password)
                            }
                            helperText={
                              formik.touched.new_password &&
                              formik.errors.new_password
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Confirm Password{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Confirm Password"
                            size="small"
                            type={showPasswordCf ? "text" : "password"}
                            {...formik.getFieldProps("confirmPassword")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={handleClickCfPassword}
                                    edge="end"
                                  >
                                    {showPasswordCf ? (
                                      <Visibility />
                                    ) : (
                                      <VisibilityOff />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            error={
                              formik.touched.confirmPassword &&
                              Boolean(formik.errors.confirmPassword)
                            }
                            helperText={
                              formik.touched.confirmPassword &&
                              formik.errors.confirmPassword
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid container spacing={3} p={"24px"}>
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        className={classes.addButton}
                        onClick={cancelFunction}
                      >
                        Reset
                      </Button>
                      <Box m={0.5}></Box>

                      <Button
                        variant="contained"
                        startIcon={
                          isLoading ? (
                            <CircularProgress
                              size={18}
                              className={classes.isLoading}
                            />
                          ) : (
                            <CheckCircleOutlineIcon/>
                          )
                        }
                        className={classes.addButton}
                        onClick={() => formik.handleSubmit()}
                        disabled={!formValid || isLoading}
                      >
                        Update
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </form>
        </FormikProvider>
      </Grid>
    </Grid>
  );
};

export default Index;
